/**
 * Evidence Page - Compliance Evidence Library
 */
import React, { useState, useEffect, useCallback } from 'react';
import { toast } from 'sonner';
import { Upload, FileCheck, Clock, ChevronRight, X, CheckCircle2, File } from 'lucide-react';
import { Card, Badge } from '../components/ui-custom/ComplianceUI';
import SearchBar from '../components/common/SearchBar';

const API_URL = process.env.REACT_APP_BACKEND_URL || '';

export default function EvidencePage() {
  const [evidence, setEvidence] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedEvidence, setSelectedEvidence] = useState(null);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadForm, setUploadForm] = useState({
    title: '',
    description: '',
    control_ids: '',
    environment: 'production'
  });
  const [uploadFile, setUploadFile] = useState(null);
  const [approving, setApproving] = useState(false);
  const [bulkApproving, setBulkApproving] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');

  const pendingCount = evidence.filter((e) => !e.passed).length;
  const passedCount = evidence.length - pendingCount;

  const filteredEvidence = (() => {
    const q = searchQuery.trim().toLowerCase();
    if (!q) return evidence;
    const tokens = q.split(/\s+/).filter(Boolean);
    return evidence.filter((item) => {
      const hay = [
        item.title || '',
        item.description || '',
        item.source || '',
        (item.control_ids || []).join(' '),
        item.file_name || '',
        item.passed ? 'passed' : 'pending',
      ].join(' ').toLowerCase();
      return tokens.every((t) => hay.includes(t));
    });
  })();

  const approveEvidence = async (id) => {
    setApproving(true);
    try {
      const res = await fetch(`${API_URL}/api/evidence/${id}/evaluate?passed=true`, {
        method: 'PATCH',
        credentials: 'include',
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        toast.error(err.detail || 'Could not mark as passed');
        return;
      }
      toast.success('Evidence marked as passed');
      setSelectedEvidence(null);
      await fetchEvidence();
    } finally {
      setApproving(false);
    }
  };

  const approveAllPending = async () => {
    const pending = evidence.filter((e) => !e.passed);
    if (pending.length === 0) {
      toast.info('No pending evidence');
      return;
    }
    if (!window.confirm(`Mark all ${pending.length} pending evidence items as Passed?`)) return;

    setBulkApproving(true);
    try {
      const results = await Promise.all(
        pending.map((e) =>
          fetch(`${API_URL}/api/evidence/${e._id}/evaluate?passed=true`, {
            method: 'PATCH',
            credentials: 'include',
          }).then((r) => r.ok)
        )
      );
      const ok = results.filter(Boolean).length;
      const fail = results.length - ok;
      if (fail === 0) toast.success(`Marked ${ok} items as passed`);
      else toast.warning(`${ok} passed, ${fail} failed`);
      await fetchEvidence();
    } finally {
      setBulkApproving(false);
    }
  };

  const fetchEvidence = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/evidence/?limit=100`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setEvidence(data.evidence || []);
      }
    } catch (error) {
      console.error('Failed to fetch evidence:', error);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchEvidence();
  }, [fetchEvidence]);

  const handleViewEvidence = (item) => {
    setSelectedEvidence(item);
  };

  const handleUploadSubmit = async () => {
    if (!uploadForm.title || !uploadForm.control_ids) {
      toast.error('Please provide a title and at least one control ID');
      return;
    }

    setUploading(true);
    try {
      let response;
      
      if (uploadFile) {
        // File upload
        const formData = new FormData();
        formData.append('file', uploadFile);
        formData.append('title', uploadForm.title);
        formData.append('control_ids', uploadForm.control_ids);
        formData.append('environment', uploadForm.environment);
        formData.append('description', uploadForm.description);
        
        response = await fetch(`${API_URL}/api/evidence/upload-file`, {
          method: 'POST',
          credentials: 'include',
          body: formData
        });
      } else {
        // Metadata only upload
        response = await fetch(`${API_URL}/api/evidence/upload`, {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            title: uploadForm.title,
            description: uploadForm.description,
            control_ids: uploadForm.control_ids.split(',').map(c => c.trim()).filter(Boolean),
            environment: uploadForm.environment,
            passed: null,
            tags: []
          })
        });
      }

      if (response.ok) {
        toast.success('Evidence uploaded successfully');
        setShowUploadModal(false);
        setUploadForm({ title: '', description: '', control_ids: '', environment: 'production' });
        setUploadFile(null);
        fetchEvidence();
      } else {
        const error = await response.json();
        toast.error(error.detail || 'Upload failed');
      }
    } catch (error) {
      toast.error('Upload failed');
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="evidence-page">
      <div className="page-header">
        <div>
          <h1>Evidence Library</h1>
          <p>
            Compliance evidence and audit trail
            {evidence.length > 0 && (
              <span style={{ marginLeft: 12, color: '#6b7280', fontSize: 13 }}>
                · {passedCount} passed · {pendingCount} pending
              </span>
            )}
          </p>
        </div>
        <div style={{ display: 'flex', gap: 8 }}>
          {pendingCount > 0 && (
            <button
              className="btn-secondary"
              onClick={approveAllPending}
              disabled={bulkApproving}
              data-testid="bulk-approve-evidence-btn"
              title={`Mark all ${pendingCount} pending items as passed`}
            >
              <CheckCircle2 size={16} />
              {bulkApproving ? 'Approving…' : `Approve ${pendingCount} pending`}
            </button>
          )}
          <button className="btn-primary" onClick={() => setShowUploadModal(true)} data-testid="upload-evidence-btn">
            <Upload size={16} /> Upload Evidence
          </button>
        </div>
      </div>

      {loading ? (
        <div className="loading-container"><div className="spinner" /></div>
      ) : evidence.length > 0 ? (
        <>
          <SearchBar
            value={searchQuery}
            onChange={setSearchQuery}
            placeholder='Find evidence — try "pentest", "CC6.1", or "passed"'
            totalCount={evidence.length}
            filteredCount={filteredEvidence.length}
            testIdPrefix="evidence-search"
          />
          {filteredEvidence.length === 0 ? (
            <Card className="empty-state" data-testid="evidence-search-empty" style={{ padding: 32, textAlign: 'center' }}>
              <h3 style={{ margin: 0 }}>No evidence matches &ldquo;{searchQuery}&rdquo;</h3>
              <p style={{ marginTop: 6, color: '#94a3b8' }}>Try a control ID, source, or status keyword.</p>
            </Card>
          ) : (
            <div className="evidence-list">
              {filteredEvidence.map((item) => (
                <Card 
                  key={item._id} 
                  className="evidence-card clickable"
                  onClick={() => handleViewEvidence(item)}
                  data-testid={`evidence-${item._id}`}
                >
                  <div className={`evidence-icon ${item.passed ? 'passed' : ''}`}>
                    {item.passed ? <CheckCircle2 size={20} /> : <Clock size={20} />}
                  </div>
                  <div className="evidence-info">
                    <div className="evidence-title">{item.title}</div>
                    <div className="evidence-controls">Controls: {item.control_ids?.join(', ')}</div>
                    <div className="evidence-meta">
                      <span>Source: {item.source}</span>
                      <span>Collected: {new Date(item.collected_at).toLocaleString()}</span>
                    </div>
                  </div>
                  <Badge color={item.passed ? '#059669' : '#d97706'}>
                    {item.passed ? 'Passed' : 'Pending'}
                  </Badge>
                  <ChevronRight size={16} className="evidence-arrow" />
                </Card>
              ))}
            </div>
          )}
        </>
      ) : (
        <Card className="empty-state">
          <FileCheck size={48} color="#6b7280" />
          <h3>No evidence collected</h3>
          <p>Evidence will appear here as monitors run and manual uploads are made.</p>
        </Card>
      )}

      {/* Evidence Detail Modal */}
      {selectedEvidence && (
        <div className="modal-overlay" onClick={() => setSelectedEvidence(null)}>
          <div className="modal-content evidence-detail" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Evidence Details</h2>
              <button className="modal-close" onClick={() => setSelectedEvidence(null)} data-testid="close-evidence-modal">
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="detail-grid">
                <div className="detail-item full-width">
                  <label>Title</label>
                  <span>{selectedEvidence.title}</span>
                </div>
                <div className="detail-item">
                  <label>Source</label>
                  <span>{selectedEvidence.source}</span>
                </div>
                <div className="detail-item">
                  <label>Status</label>
                  <Badge color={selectedEvidence.passed ? '#059669' : '#d97706'}>
                    {selectedEvidence.passed ? 'Passed' : 'Pending Review'}
                  </Badge>
                </div>
                <div className="detail-item">
                  <label>Collected At</label>
                  <span>{new Date(selectedEvidence.collected_at).toLocaleString()}</span>
                </div>
                <div className="detail-item">
                  <label>Expires</label>
                  <span>{selectedEvidence.expires_at ? new Date(selectedEvidence.expires_at).toLocaleDateString() : 'Never'}</span>
                </div>
                <div className="detail-item full-width">
                  <label>Related Controls</label>
                  <div className="control-badges">
                    {selectedEvidence.control_ids?.map(cid => (
                      <Badge key={cid} color="#6b7280">{cid}</Badge>
                    ))}
                  </div>
                </div>
              </div>
              
              {selectedEvidence.description && (
                <div className="evidence-description">
                  <label>Description</label>
                  <p>{selectedEvidence.description}</p>
                </div>
              )}

              {selectedEvidence.data && (
                <div className="evidence-data">
                  <label>Evidence Data</label>
                  <pre className="json-block">{JSON.stringify(selectedEvidence.data, null, 2)}</pre>
                </div>
              )}
            </div>
            <div className="modal-footer">
              {!selectedEvidence.passed && (
                <button
                  className="btn-primary"
                  onClick={() => approveEvidence(selectedEvidence._id)}
                  disabled={approving}
                  data-testid="approve-evidence-btn"
                >
                  <CheckCircle2 size={16} />
                  {approving ? 'Marking…' : 'Mark as Passed'}
                </button>
              )}
              <button className="btn-secondary" onClick={() => setSelectedEvidence(null)}>Close</button>
            </div>
          </div>
        </div>
      )}

      {/* Upload Evidence Modal */}
      {showUploadModal && (
        <div className="modal-overlay" onClick={() => setShowUploadModal(false)}>
          <div className="modal-content" onClick={e => e.stopPropagation()}>
            <div className="modal-header">
              <h2>Upload Evidence</h2>
              <button className="modal-close" onClick={() => setShowUploadModal(false)}>
                <X size={20} />
              </button>
            </div>
            <div className="modal-body">
              <div className="form-group">
                <label>Title *</label>
                <input
                  type="text"
                  placeholder="e.g., Q1 2026 Access Review Export"
                  value={uploadForm.title}
                  onChange={e => setUploadForm({...uploadForm, title: e.target.value})}
                  data-testid="evidence-title-input"
                />
              </div>
              
              <div className="form-group">
                <label>Control IDs * (comma-separated)</label>
                <input
                  type="text"
                  placeholder="e.g., CC6.1, CC6.2, CC6.3"
                  value={uploadForm.control_ids}
                  onChange={e => setUploadForm({...uploadForm, control_ids: e.target.value})}
                  data-testid="evidence-controls-input"
                />
              </div>
              
              <div className="form-group">
                <label>Environment</label>
                <select
                  value={uploadForm.environment}
                  onChange={e => setUploadForm({...uploadForm, environment: e.target.value})}
                >
                  <option value="production">Production</option>
                  <option value="staging">Staging</option>
                  <option value="organizational">Organizational</option>
                </select>
              </div>
              
              <div className="form-group">
                <label>Description</label>
                <textarea
                  placeholder="Optional description of the evidence..."
                  value={uploadForm.description}
                  onChange={e => setUploadForm({...uploadForm, description: e.target.value})}
                  rows={3}
                />
              </div>
              
              <div className="form-group">
                <label>Attach File (Optional)</label>
                <div className="file-upload-zone">
                  {uploadFile ? (
                    <div className="file-selected">
                      <File size={20} />
                      <span>{uploadFile.name}</span>
                      <button onClick={() => setUploadFile(null)} className="remove-file">
                        <X size={16} />
                      </button>
                    </div>
                  ) : (
                    <label className="file-drop-label">
                      <Upload size={24} />
                      <span>Click to select or drag & drop</span>
                      <input
                        type="file"
                        onChange={e => setUploadFile(e.target.files?.[0] || null)}
                        style={{ display: 'none' }}
                      />
                    </label>
                  )}
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-secondary" onClick={() => setShowUploadModal(false)}>Cancel</button>
              <button 
                className="btn-primary" 
                onClick={handleUploadSubmit}
                disabled={uploading}
              >
                {uploading ? 'Uploading...' : 'Upload Evidence'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
